import { useNuxtApp, useRequestHeaders, useRuntimeConfig } from "#imports";
import { useCookie } from "nuxt/app";
import cloneDeep from "lodash-es/cloneDeep";
import { $fetch, FetchError } from "ofetch";
import { withBase } from "ufo";
import { useBackendUrl } from "./useBackendUrl.js";
import { useSpanWrap } from "./useSpanWrap.js";
import { allowedHeaderValues } from "../utils/api-cookie.js";
export { FetchError as OFetchError } from "ofetch";
function makeUniversalUrl(url, baseURL) {
  const isAbsolute = /^(?:[a-z+]+:)?\/\//i.test(url);
  if (!isAbsolute && import.meta.server) {
    const host = guestHost();
    const urlWithBase = withBase(url, baseURL ?? "");
    return new URL(urlWithBase, host).toString();
  }
  return url;
}
function prepareOptions(nuxtApp, opts) {
  const options = cloneDeep(opts);
  const runtimeConfig = useRuntimeConfig();
  let headersToForwardFromClient = {};
  if (import.meta.server) {
    headersToForwardFromClient = useRequestHeaders();
    headersToForwardFromClient[`x-malt-ssr`] = "true";
    if (nuxtApp.$apiCookies) {
      const { cookieValues } = nuxtApp.$apiCookies;
      for (const cookieName of allowedHeaderValues) {
        if (cookieValues[cookieName]) {
          headersToForwardFromClient.cookie = appendToCookieString(headersToForwardFromClient.cookie, cookieName, cookieValues[cookieName]);
        }
      }
      if (cookieValues["XSRF-TOKEN"]) {
        if (!["GET", "HEAD", "TRACE", "OPTIONS"].includes(opts.method || "")) {
          headersToForwardFromClient["x-xsrf-token"] = cookieValues["XSRF-TOKEN"];
        }
      }
    } else {
      console.warn(new Error('The "backend-api-cookies" plugin must be initialized before reaching this point'));
    }
    if (headersToForwardFromClient.accept?.includes("text/html")) {
      headersToForwardFromClient.accept = "*/*";
    }
    delete headersToForwardFromClient["accept-encoding"];
    const fetchTimeout = opts.timeout || runtimeConfig.ssrFetchTimeout;
    if (!!fetchTimeout && !isNaN(fetchTimeout)) {
      if (opts.timeout) {
        options.timeout = +fetchTimeout;
      } else {
        options.timeout = +fetchTimeout * 1e3;
      }
    }
  }
  const csrf = useCookie("XSRF-TOKEN");
  const { appVersion } = runtimeConfig.public;
  options.headers = {
    ...{ Accept: "application/json" },
    ...{ "cache-control": runtimeConfig.public.noStoreCache ? "no-store" : "" },
    ...options?.headers && options.headers,
    ...csrf.value && { "x-xsrf-token": csrf.value },
    ...appVersion && { "x-app-version": appVersion },
    ...headersToForwardFromClient
  };
  return options;
}
function appendToCookieString(cookieString, cookieKey, cookieValue) {
  const cookieParts = (cookieString || "").split(";").filter((part) => !part.trim().startsWith(`${cookieKey}=`));
  const newCookie = `${cookieKey}=${cookieValue}`;
  return [...cookieParts, newCookie].join("; ");
}
function updateServerApiCookies(nuxtApp, response) {
  if (!import.meta.server || !response) {
    return;
  }
  if (nuxtApp.$apiCookies) {
    const { registerServerSideSetCookies } = nuxtApp.$apiCookies;
    registerServerSideSetCookies(response);
  } else {
    console.warn(new Error('The "backend-api-cookies" plugin must be initialized before reaching this point'));
  }
}
export function guestHost() {
  if (import.meta.server) {
    const headers = useRequestHeaders();
    if (!headers.host) {
      return "https://www.malt.fr";
    }
    return `https://${headers.host}`;
  } else {
    return window.location.origin;
  }
}
export function createUniversalFetch(options) {
  const universalFetchRaw = createUniversalFetchRaw(options);
  async function universalFetch(url, opts = { method: "GET" }) {
    const response = await universalFetchRaw(url, opts);
    return response._data;
  }
  return universalFetch;
}
export function createUniversalFetchRaw(options) {
  const $fetchInstance = $fetch.create(options);
  return createUniversalFetchRawWith$FetchInstance($fetchInstance.raw);
}
function createUniversalFetchRawWith$FetchInstance(fetchClientRaw) {
  async function universalFetchRaw(url, opts = { method: "GET" }) {
    const universalURL = makeUniversalUrl(url, opts.baseURL);
    const nuxtApp = useNuxtApp();
    const fetchOptions = prepareOptions(nuxtApp, opts);
    try {
      if (import.meta.server && process.env.FETCH_DEBUG) {
        const prefix = process.dev ? "\x1B[33m%s\x1B[0m" : "";
        console.log(`${prefix}${universalURL}
${JSON.stringify(fetchOptions, null, 2)}`, "useSpanWrap");
      }
      const runtimeConfig = useRuntimeConfig();
      if (runtimeConfig.public.fetchDebug && import.meta.client) {
        const timestamp = (/* @__PURE__ */ new Date()).toISOString();
        console.log(`[${timestamp}] Fetch Request:`, {
          url: universalURL,
          method: fetchOptions.method,
          headers: fetchOptions.headers,
          body: fetchOptions.body,
          timeout: fetchOptions.timeout
        });
        fetchOptions.onRequest = (request) => {
          console.log(`[${timestamp}] onRequest`, request, request.response);
        };
        fetchOptions.onResponse = (response2) => {
          console.log(`[${timestamp}] onResponse`, response2);
        };
        fetchOptions.onRequestError = (error) => {
          console.log(`[${timestamp}] onRequestError`, error);
        };
        fetchOptions.onResponseError = (error) => {
          console.log(`[${timestamp}] onResponseError`, error);
        };
      }
      const response = await useSpanWrap(() => fetchClientRaw(universalURL, fetchOptions));
      if (runtimeConfig.public.fetchDebug && import.meta.client) {
        const timestamp = (/* @__PURE__ */ new Date()).toISOString();
        console.log(`[${timestamp}] Fetch Response:`, {
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries()),
          url: response.url
        });
      }
      updateServerApiCookies(nuxtApp, response);
      return response;
    } catch (error) {
      if (error instanceof FetchError) {
        updateServerApiCookies(nuxtApp, error.response);
        if (import.meta.client && typeof window !== "undefined" && (error.statusCode === 403 || error.statusCode === 401)) {
          document.dispatchEvent(new Event("user-may-be-disconnected"));
        }
      }
      return Promise.reject(error);
    }
  }
  return universalFetchRaw;
}
export async function useUniversalFetchRaw(url, opts = { method: "GET" }) {
  const baseURL = useBackendUrl();
  const actualImplementation = createUniversalFetchRawWith$FetchInstance($fetch.raw);
  const response = await actualImplementation(url, { ...opts, baseURL: opts.baseURL ?? baseURL });
  if (import.meta.client && useRuntimeConfig().public.fetchDebug) {
    const timestamp = (/* @__PURE__ */ new Date()).toISOString();
    console.log(`[${timestamp}] Fetch Response from useUniversalFetchRaw`, response);
  }
  return response;
}
export async function useUniversalFetch(url, opts = { method: "GET" }) {
  const response = await useUniversalFetchRaw(url, opts);
  if (import.meta.client && useRuntimeConfig().public.fetchDebug) {
    const timestamp = (/* @__PURE__ */ new Date()).toISOString();
    console.log(`[${timestamp}] Fetch from useUniversalFetch`, response);
  }
  return response._data;
}
export function useUniversalPost(url, opts = {}) {
  return useUniversalFetch(url, { ...opts, method: "POST" });
}
export function useUniversalPut(url, opts = {}) {
  return useUniversalFetch(url, { ...opts, method: "PUT" });
}
export function useUniversalDelete(url, opts = {}) {
  return useUniversalFetch(url, { ...opts, method: "DELETE" });
}
